/* eslint-disable no-console */
import axios from 'axios';
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';

import { getAuthErrorMessage } from '../utils/authErrors';

// ============================================================================
// Context 정의 (v2.0 - 성능 최적화를 위한 분리)
// ============================================================================

// 기존 AuthContext는 호환성을 위해 유지 (deprecated)
const AuthContext = createContext();

// 새로운 분리된 Context
const UserContext = createContext();
const AuthActionsContext = createContext();

export { AuthContext, UserContext, AuthActionsContext };

// ============================================================================
// Hooks
// ============================================================================

/**
 * useUser - 사용자 상태만 필요한 컴포넌트용
 * user 변경 시에만 리렌더링됨
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within an AuthProvider');
  }
  return context;
};

/**
 * useAuthActions - 액션 함수만 필요한 컴포넌트용
 * 액션 함수는 useCallback으로 안정화되어 거의 리렌더링되지 않음
 */
export const useAuthActions = () => {
  const context = useContext(AuthActionsContext);
  if (!context) {
    throw new Error('useAuthActions must be used within an AuthProvider');
  }
  return context;
};

/**
 * useAuth - 기존 코드 호환성 레이어
 * 가능하면 useUser() 또는 useAuthActions()를 사용하세요
 */
export const useAuth = () => {
  const userCtx = useContext(UserContext);
  const actionsCtx = useContext(AuthActionsContext);

  if (!userCtx || !actionsCtx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  // useMemo로 병합된 객체 메모이제이션
  return useMemo(() => ({
    ...userCtx,
    ...actionsCtx,
    api, // 호환성을 위해 포함
  }), [userCtx, actionsCtx]);
};

// ============================================================================
// API Instance
// ============================================================================

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// 인증 요청 공통 설정
// - withCredentials: refresh 토큰은 HttpOnly 쿠키(ss_refresh)라 브라우저가 자동 전송해야 한다
// - X-Requested-With: 서버가 refresh/logout 의 CSRF 방어로 요구하는 헤더 (cross-site 폼으로는 설정 불가)
const AUTH_REQUEST_HEADERS = { 'X-Requested-With': 'XMLHttpRequest' };

// Axios 인스턴스 생성
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 120초로 증가 (과거 데이터 동기화를 위해)
  withCredentials: true,
  headers: AUTH_REQUEST_HEADERS,
});

// ============================================================================
// Access Token 보관 (메모리 전용 - localStorage/sessionStorage 에 저장하지 않음)
// ============================================================================

// XSS 로 탈취 가능한 저장소를 피하기 위해 access 토큰은 모듈 변수에만 둔다.
// 새로고침하면 사라지며, 앱 시작 시 refresh 쿠키로 복구한다.
let accessToken = null;
const setAccessToken = (value) => {
  accessToken = value || null;
};

// 이전 버전이 localStorage 에 남긴 토큰 키 (1회 정리 대상)
const LEGACY_TOKEN_KEYS = ['auth_token', 'refresh_token'];
const clearLegacyTokens = () => {
  try {
    LEGACY_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage 접근 불가 환경 (사생활 보호 모드 등) - 무시
  }
};

// 다른 탭과 로그아웃 동기화 (BroadcastChannel 미지원 브라우저는 동기화 없이 동작)
const AUTH_CHANNEL_NAME = 'stackstock-auth';
const AUTH_LOGOUT_MESSAGE = 'logout';

// ============================================================================
// Token Refresh 유틸리티 (모듈 레벨 - React 상태와 독립적)
// ============================================================================

// 동시 401 요청 처리를 위한 Queue
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

// refresh 쿠키로 새 Access Token 발급 (React 상태 독립적)
// - raw axios 사용: api 인스턴스의 인터셉터(Authorization 첨부, 401 재시도)를 거치지 않는다
// - Authorization 헤더 없음: 서버가 쿠키로 인증하며 쿠키는 회전되어 자동 갱신된다
// - 진행 중인 갱신은 공유한다 (StrictMode 이중 effect / 타이머·인터셉터 중복 호출 방지)
let refreshInFlight = null;
const attemptTokenRefresh = () => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const response = await axios.post(
      `${API_BASE_URL}/api/v1/auth/refresh`,
      {},
      { withCredentials: true, headers: AUTH_REQUEST_HEADERS, timeout: 30000 },
    );

    const newToken = response.data?.access_token;
    if (newToken) {
      setAccessToken(newToken);
      return newToken;
    }
    return null;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
};

// 인증된 API 인스턴스 export
export { api };

// ============================================================================
// AuthProvider
// ============================================================================

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // access 토큰은 메모리에만 보관 (초기값 null - 시작 시 refresh 쿠키로 복구)
  const [token, setToken] = useState(null);
  // 시작 시 refresh 결과가 나올 때까지 true 유지 → ProtectedRoute 가 로그인 화면으로 보내지 않음
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const authChannelRef = useRef(null);

  // ============================================================================
  // 액션 함수 (useCallback으로 안정화)
  // ============================================================================

  // 내부 로그아웃 함수 (인터셉터/다른 탭 알림에서 사용) - 로컬 상태만 정리, 서버 호출 없음
  const handleLogout = useCallback(async () => {
    setAccessToken(null);
    setToken(null);
    setUser(null);
  }, []);

  // handleLogout을 ref로 유지 — interceptor가 항상 최신 버전을 참조하도록
  const handleLogoutRef = useRef(handleLogout);
  useEffect(() => {
    handleLogoutRef.current = handleLogout;
  }, [handleLogout]);

  // API 요청 인터셉터: 모든 요청에 토큰 자동 첨부
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        // 모듈 변수에서 직접 읽어 setToken 반영 지연과 무관하게 항상 최신 토큰 사용
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // API 응답 인터셉터: 인증 오류 처리 + 네트워크 상태 감지
    const responseInterceptor = api.interceptors.response.use(
      (response) => {
        // API 요청 성공 시 온라인 이벤트 발생
        window.dispatchEvent(new CustomEvent('api-success'));
        return response;
      },
      async (error) => {
        // 취소된 요청(AbortController abort, 화면 전환, StrictMode 이중 마운트 등)은 네트워크 오류가 아니다.
        // 오프라인 이벤트·에러 로그 없이 그대로 reject 해 호출부의 기존 취소 처리를 유지한다.
        if (error?.code === 'ERR_CANCELED' || axios.isCancel?.(error)) {
          return Promise.reject(error);
        }

        // 네트워크 에러 상세 로깅
        if (!error.response) {
          console.error('Network error details:', {
            message: error.message,
            code: error.code,
            config: error.config?.url,
            timeout: error.config?.timeout,
          });

          // 네트워크 오류 시 오프라인 이벤트 발생
          window.dispatchEvent(new CustomEvent('api-network-error', {
            detail: { error },
          }));

          // 타임아웃 에러 처리
          if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            error.code = 'TIMEOUT_ERROR';
            error.message = 'Request timeout - server is taking too long to respond';
          } else {
            error.code = 'NETWORK_ERROR';
          }
        }

        const originalRequest = error.config;

        // logout/login 요청의 401 은 refresh/재시도 대상이 아님
        // (logout: 로컬 로그아웃은 logout() 이 진행, login: 비밀번호 오류가 refresh 를 유발하면 안 됨)
        if (
          error.response?.status === 401
          && (originalRequest?.url?.includes('/auth/logout') || originalRequest?.url?.includes('/auth/login'))
        ) {
          return Promise.reject(error);
        }

        // 401 에러 처리: refresh 시도 후 원래 요청 재시도
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          // refresh 엔드포인트 자체가 401이면 바로 로그아웃
          if (originalRequest.url?.includes('/auth/refresh')) {
            handleLogoutRef.current();
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          if (isRefreshing) {
            // 이미 refresh 중이면 queue에 추가하고 완료 대기
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return api(originalRequest);
            }).catch((err) => Promise.reject(err));
          }

          isRefreshing = true;

          try {
            const newToken = await attemptTokenRefresh();
            if (newToken) {
              setToken(newToken);
              processQueue(null, newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return api(originalRequest);
            } else {
              processQueue(error, null);
              handleLogoutRef.current();
            }
          } catch (refreshError) {
            processQueue(refreshError, null);
            handleLogoutRef.current();
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);  // 토큰은 모듈 변수에서 읽고 handleLogoutRef.current() 를 사용 — stale closure 없음

  // 초기 로드: 옛 localStorage 토큰 정리 + refresh 쿠키로 세션 복구
  // 메모리에 토큰이 없으므로 항상 refresh 를 먼저 시도한다. 완료 전까지 loading=true 유지.
  useEffect(() => {
    let cancelled = false;

    // 이전 버전이 남긴 토큰은 XSS 노출면이므로 즉시 삭제 (재로그인 1회 필요)
    clearLegacyTokens();

    const restoreSession = async () => {
      try {
        const newToken = await attemptTokenRefresh();
        if (!newToken) {
          return; // 로그인 상태 아님
        }
        if (cancelled) return;
        setToken(newToken);

        const response = await api.get('/api/v1/auth/me');
        if (cancelled) return;
        setUser(response.data.data);
      } catch (error) {
        if (cancelled) return;
        if (error.response?.status === 401) {
          // refresh 쿠키 없음/만료 - 정상적인 비로그인 상태
          console.info('No valid refresh session on startup');
        } else {
          console.error('Session restore failed:', error.response?.status, error.message);
        }
        setAccessToken(null);
        setToken(null);
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, []);

  // 다른 탭과 로그아웃 동기화 (BroadcastChannel 미지원 시 무시)
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;

    let channel = null;
    try {
      channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
      channel.onmessage = (event) => {
        if (event?.data === AUTH_LOGOUT_MESSAGE) {
          // 다른 탭에서 로그아웃 - 메모리 토큰/사용자만 비움 (서버 호출·재전파 없음)
          handleLogoutRef.current();
        }
      };
    } catch {
      channel = null;
    }
    authChannelRef.current = channel;

    return () => {
      authChannelRef.current = null;
      try {
        channel?.close();
      } catch {
        // ignore
      }
    };
  }, []);

  // 자동 토큰 갱신 타이머 (25분마다 - Access Token 30분 만료 전 갱신)
  useEffect(() => {
    if (!token) return undefined;

    const interval = setInterval(async () => {
      try {
        const newToken = await attemptTokenRefresh();
        if (newToken) {
          setToken(newToken);
          console.info('Access token auto-refreshed');
        } else {
          handleLogout();
        }
      } catch {
        handleLogout();
      }
    }, 25 * 60 * 1000); // 25분

    return () => clearInterval(interval);
  }, [token, handleLogout]);

  // 회원가입
  const register = useCallback(async ({ email, username, password }) => {
    try {
      const response = await api.post('/api/v1/auth/register', {
        email,
        username,
        password,
      });

      if (response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data,
        };
      }

      throw new Error(response.data.message || 'Registration failed');
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Registration failed');

      return { success: false, message };
    }
  }, []);

  // 로그인
  const login = useCallback(async (emailOrUsername, password) => {
    try {
      const response = await api.post('/api/v1/auth/login', {
        email_or_username: emailOrUsername,
        password,
      });

      if (response.data.access_token) {
        // refresh 는 서버가 HttpOnly 쿠키로 설정 - access 만 메모리에 보관
        const authToken = response.data.access_token;
        setAccessToken(authToken);
        setToken(authToken);

        // 사용자 정보 가져오기
        const userResponse = await api.get('/api/v1/auth/me', {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setUser(userResponse.data.data);

        // 첫 로그인인지 확인하여 온보딩 표시 여부 결정
        const hasCompletedOnboarding = localStorage.getItem('onboarding_completed');
        if (!hasCompletedOnboarding) {
          setShowOnboarding(true);
        }

        return { success: true, message: 'Login successful' };
      }
      throw new Error('No access token received');
    } catch (error) {
      const message = getAuthErrorMessage(error, 'Login failed');
      return { success: false, message };
    }
  }, []);

  // 로그아웃
  const logout = useCallback(async () => {
    try {
      // access 는 인터셉터가 첨부, refresh 쿠키는 브라우저가 자동 전송 (서버가 쿠키 삭제 + 폐기)
      await api.post('/api/v1/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      handleLogout(); // 서버 실패해도 로컬 로그아웃
      try {
        authChannelRef.current?.postMessage(AUTH_LOGOUT_MESSAGE); // 다른 탭 동기화
      } catch {
        // ignore
      }
    }
  }, [handleLogout]);

  // 비밀번호 변경
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const response = await api.put('/api/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      // 비밀번호 변경 시 이전 토큰이 즉시 무효화되므로 응답의 새 토큰으로 교체
      const newTokens = response.data?.data;
      // (새 refresh 는 서버가 쿠키로 교체 - access 만 메모리에 저장)
      if (newTokens?.access_token) {
        setAccessToken(newTokens.access_token);
        setToken(newTokens.access_token);
      }
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Password change failed';
      return { success: false, message };
    }
  }, []);

  // 토큰 갱신 (외부 호출용 - React 상태 업데이트 포함)
  const refreshTokenFn = useCallback(async () => {
    try {
      const newToken = await attemptTokenRefresh();
      if (newToken) {
        setToken(newToken);
        return true;
      }
      handleLogout();
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      handleLogout();
      return false;
    }
  }, [handleLogout]);

  // 프로필 업데이트 (닉네임 등)
  const updateProfile = useCallback(async (profileData) => {
    try {
      const response = await api.put('/api/v1/auth/profile', profileData);
      if (response.data.success) {
        // 사용자 상태 업데이트
        setUser(response.data.data);
        return { success: true, message: response.data.message };
      }
      throw new Error(response.data.message || 'Profile update failed');
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Profile update failed';
      return { success: false, message };
    }
  }, []);

  // 온보딩 관련 함수들
  const closeOnboarding = useCallback(() => {
    setShowOnboarding(false);
  }, []);

  const skipOnboarding = useCallback(() => {
    localStorage.setItem('onboarding_completed', 'true');
    setShowOnboarding(false);
  }, []);

  // ============================================================================
  // Context Values (useMemo로 최적화)
  // ============================================================================

  // UserContext value - 사용자 상태
  const userValue = useMemo(() => ({
    user,
    token,
    loading,
    isAuthenticated: !!user,
    showOnboarding,
  }), [user, token, loading, showOnboarding]);

  // AuthActionsContext value - 액션 함수
  const actionsValue = useMemo(() => ({
    login,
    register,
    logout,
    changePassword,
    updateProfile,
    refreshToken: refreshTokenFn,
    closeOnboarding,
    skipOnboarding,
  }), [login, register, logout, changePassword, updateProfile, refreshTokenFn, closeOnboarding, skipOnboarding]);

  // 기존 AuthContext value (호환성 레이어)
  const legacyValue = useMemo(() => ({
    ...userValue,
    ...actionsValue,
    api,
  }), [userValue, actionsValue]);

  return (
    <AuthContext.Provider value={legacyValue}>
      <UserContext.Provider value={userValue}>
        <AuthActionsContext.Provider value={actionsValue}>
          {children}
        </AuthActionsContext.Provider>
      </UserContext.Provider>
    </AuthContext.Provider>
  );
};
